import React, { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, LineChart, Line,
} from "recharts";
import {
  KeyRound, Plus, RotateCw, Ban, Copy, Terminal, Webhook, Activity, Gauge,
  BookOpen, Shield, Download, Eye, EyeOff, PauseCircle, PlayCircle, Trash2, ExternalLink,
  ListChecks, CheckCircle2, XCircle,
} from "lucide-react";

import { api, apiErrorText } from "@/lib/api";
import {
  VLKPageHeader, VLKCard, VLKCardHeader, VLKCardTitle, VLKCardContent, VLKCardDescription,
  VLKButton, VLKInput, VLKLabel, VLKSelect, VLKStatusBadge, VLKKPICard, VLKTabs,
  VLKAlertDialog, VLKDataTable, VLKEmptyState, VLKTextarea, VLKSkeleton, toast,
} from "@/components/vlk";
import { useAuth } from "@/context/AuthContext";
import { cn } from "@/lib/utils";

/* ============================== Reveal box ============================== */
function OneTimeReveal({ label, value, onDismiss }) {
  const [visible, setVisible] = useState(true);
  const copy = async () => {
    try {
      await navigator.clipboard?.writeText(value);
      toast.success("Copied to clipboard");
    } catch { toast.error("Copy blocked — select manually"); }
  };
  return (
    <div className="border-2 border-warning/40 bg-warning/5 rounded p-4 space-y-3">
      <div className="flex items-center gap-2 text-warning">
        <Shield className="h-4 w-4" />
        <span className="text-[10px] uppercase tracking-widest font-mono font-semibold">Store this now — shown once</span>
      </div>
      <div className="space-y-1.5">
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
        <div className="flex items-center gap-2">
          <code className="flex-1 font-mono text-sm bg-background border border-border rounded-sm px-3 py-2 select-all overflow-x-auto">
            {visible ? value : "•".repeat(Math.min(value.length, 40))}
          </code>
          <VLKButton size="icon" variant="outline" onClick={() => setVisible(!visible)} aria-label="Toggle visibility">
            {visible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </VLKButton>
          <VLKButton size="icon" variant="outline" onClick={copy} aria-label="Copy" data-testid="dev-reveal-copy">
            <Copy className="h-4 w-4" />
          </VLKButton>
        </div>
        <div className="text-xs text-muted-foreground">
          The server stores only a bcrypt hash. If you lose this value, rotate the credential.
        </div>
      </div>
      <VLKButton size="sm" variant="ghost" onClick={onDismiss}>I've stored it</VLKButton>
    </div>
  );
}

/* ============================== Credentials Tab ============================== */
function CredentialsTab({ institutions }) {
  const qc = useQueryClient();
  const [issueOpen, setIssueOpen] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState(null);
  const [revokeReason, setRevokeReason] = useState("");
  const [reveal, setReveal] = useState(null); // { label, value }
  const [busy, setBusy] = useState(false);

  // Issue form state
  const [name, setName] = useState("");
  const [inst, setInst] = useState("");
  const [env, setEnv] = useState("TEST");
  const [scopes, setScopes] = useState([]);

  const { data: credsData } = useQuery({
    queryKey: ["dev-credentials"],
    queryFn: () => api.get("/developer/credentials").then(r => r.data),
  });
  const { data: scopesData } = useQuery({
    queryKey: ["dev-scopes"],
    queryFn: () => api.get("/developer/scopes").then(r => r.data),
  });

  const toggleScope = (key) =>
    setScopes((prev) => (prev.includes(key) ? prev.filter(s => s !== key) : [...prev, key]));

  const issue = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/developer/credentials", {
        name, vlk_inst_id: inst, env, scopes,
      });
      setReveal({ label: `API key · ${data.credential_id}`, value: data.full_key_reveal });
      setIssueOpen(false); setName(""); setInst(""); setScopes([]);
      toast.success("Credential issued");
      qc.invalidateQueries({ queryKey: ["dev-credentials"] });
    } catch (e) { toast.error("Failed", { description: apiErrorText(e) }); }
    finally { setBusy(false); }
  };

  const rotate = async (cred) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/developer/credentials/${cred.credential_id}/rotate`);
      setReveal({ label: `Rotated key · new id ${data.credential_id}`, value: data.full_key_reveal });
      toast.success("Credential rotated", { description: "Old key marked ROTATED." });
      qc.invalidateQueries({ queryKey: ["dev-credentials"] });
    } catch (e) { toast.error("Rotate failed", { description: apiErrorText(e) }); }
    finally { setBusy(false); }
  };

  const revoke = async () => {
    setBusy(true);
    try {
      await api.post(`/developer/credentials/${revokeTarget.credential_id}/revoke`, { reason: revokeReason });
      toast.success("Credential revoked");
      setRevokeTarget(null); setRevokeReason("");
      qc.invalidateQueries({ queryKey: ["dev-credentials"] });
    } catch (e) { toast.error("Revoke failed", { description: apiErrorText(e) }); }
    finally { setBusy(false); }
  };

  const rows = credsData?.items || [];

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="font-display text-lg">API credentials</div>
          <div className="text-xs text-muted-foreground">Prefix + last 4 shown here. Full key is revealed once at issue/rotate.</div>
        </div>
        <VLKButton variant="primary" onClick={() => { setIssueOpen(true); setName(""); setInst(institutions[0]?.vlk_inst_id || ""); setEnv("TEST"); setScopes([]); }} data-testid="dev-issue-btn">
          <Plus className="h-4 w-4" />Issue credential
        </VLKButton>
      </div>

      {reveal && <div className="mb-4"><OneTimeReveal label={reveal.label} value={reveal.value} onDismiss={() => setReveal(null)} /></div>}

      <VLKDataTable
        columns={[
          { key: "name", header: "Name", render: (r) => (
            <div>
              <div className="font-medium">{r.name}</div>
              <div className="font-mono text-[10px] text-muted-foreground">{r.credential_id}</div>
            </div>
          )},
          { key: "env", header: "Env", render: (r) => (
            <VLKStatusBadge status={r.env === "LIVE" ? "locked" : "verified"}>{r.env === "LIVE" ? "PROD" : "SANDBOX"}</VLKStatusBadge>
          )},
          { key: "key_prefix", header: "Key", mono: true, render: (r) => <span className="font-mono text-xs">{r.key_prefix}</span> },
          { key: "institution_name", header: "Institution", render: (r) => <span className="text-sm">{r.institution_name}</span> },
          { key: "scopes", header: "Scopes", render: (r) => (
            <div className="flex flex-wrap gap-1 max-w-[240px]">
              {(r.scopes || []).slice(0, 3).map(s => (
                <span key={s} className="font-mono text-[10px] uppercase text-muted-foreground border border-border px-1.5 py-0.5 rounded-[2px]">{s}</span>
              ))}
              {(r.scopes || []).length > 3 && <span className="text-[10px] text-muted-foreground">+{r.scopes.length - 3}</span>}
            </div>
          )},
          { key: "status", header: "Status", render: (r) => <VLKStatusBadge status={r.status === "ACTIVE" ? "active" : r.status === "REVOKED" ? "rejected" : "pending"}>{r.status}</VLKStatusBadge> },
          { key: "last_used_at", header: "Last used", mono: true, render: (r) => r.last_used_at
              ? <span className="text-xs text-muted-foreground">{new Date(r.last_used_at).toLocaleString()}</span>
              : <span className="text-xs text-muted-foreground">never</span>
          },
          { key: "actions", header: "", align: "right", render: (r) => (
            <div className="flex gap-1 justify-end">
              {r.status === "ACTIVE" && (
                <>
                  <VLKButton size="sm" variant="ghost" onClick={() => rotate(r)} data-testid={`dev-rotate-${r.credential_id}`}>
                    <RotateCw className="h-3.5 w-3.5" />Rotate
                  </VLKButton>
                  <VLKButton size="sm" variant="ghost" onClick={() => { setRevokeTarget(r); setRevokeReason(""); }} data-testid={`dev-revoke-${r.credential_id}`}>
                    <Ban className="h-3.5 w-3.5" />Revoke
                  </VLKButton>
                </>
              )}
            </div>
          )},
        ]}
        rows={rows}
        rowKey={(r) => r.credential_id}
        empty="No credentials issued yet."
      />

      {/* Issue dialog */}
      <VLKAlertDialog
        open={issueOpen}
        onOpenChange={setIssueOpen}
        title="Issue new API credential"
        actions={
          <>
            <VLKButton variant="ghost" onClick={() => setIssueOpen(false)}>Cancel</VLKButton>
            <VLKButton variant="primary" onClick={issue} loading={busy} disabled={!name || !inst} data-testid="dev-issue-submit">
              <KeyRound className="h-4 w-4" />Issue
            </VLKButton>
          </>
        }
      >
        <div className="space-y-3">
          <div className="space-y-1.5">
            <VLKLabel>Name</VLKLabel>
            <VLKInput value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Production KYC pipeline" data-testid="dev-issue-name" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <VLKLabel>Institution</VLKLabel>
              <VLKSelect value={inst} onChange={(e) => setInst(e.target.value)} data-testid="dev-issue-inst">
                {institutions.map(i => <option key={i.vlk_inst_id} value={i.vlk_inst_id}>{i.name}</option>)}
              </VLKSelect>
            </div>
            <div className="space-y-1.5">
              <VLKLabel>Environment</VLKLabel>
              <VLKSelect value={env} onChange={(e) => setEnv(e.target.value)} data-testid="dev-issue-env">
                <option value="TEST">Sandbox (TEST)</option>
                <option value="LIVE">Production (LIVE)</option>
              </VLKSelect>
            </div>
          </div>
          <div className="space-y-1.5">
            <VLKLabel>Scopes ({scopes.length} selected)</VLKLabel>
            <div className="border border-border rounded-sm p-3 max-h-60 overflow-y-auto space-y-1.5">
              {(scopesData?.scopes || []).map(s => (
                <label key={s.key} className="flex items-start gap-2 cursor-pointer hover:bg-secondary/30 rounded-sm p-1.5">
                  <input type="checkbox" checked={scopes.includes(s.key)} onChange={() => toggleScope(s.key)} className="mt-1 accent-foreground" data-testid={`dev-scope-${s.key.replace(/\./g, '-')}`} />
                  <div className="min-w-0">
                    <div className="font-mono text-xs">{s.key}</div>
                    <div className="text-xs text-muted-foreground">{s.description}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>
        </div>
      </VLKAlertDialog>

      {/* Revoke dialog */}
      <VLKAlertDialog
        open={!!revokeTarget}
        onOpenChange={() => setRevokeTarget(null)}
        title={`Revoke ${revokeTarget?.name}`}
        tone="destructive"
        actions={
          <>
            <VLKButton variant="ghost" onClick={() => setRevokeTarget(null)}>Cancel</VLKButton>
            <VLKButton variant="destructive" onClick={revoke} loading={busy} disabled={revokeReason.length < 3} data-testid="dev-revoke-submit">Confirm revoke</VLKButton>
          </>
        }
      >
        <div className="space-y-3">
          <div className="text-sm">Revocation is <span className="text-destructive font-medium">immediate and irreversible</span>. Any active integrations using this key will fail with 401 on the next call.</div>
          <div className="space-y-1.5">
            <VLKLabel>Reason (auditable)</VLKLabel>
            <VLKTextarea value={revokeReason} onChange={(e) => setRevokeReason(e.target.value)} rows={3} placeholder="Compromised key / Key retirement / Employee offboarding…" data-testid="dev-revoke-reason" />
          </div>
        </div>
      </VLKAlertDialog>
    </div>
  );
}

/* ============================== Webhook Deliveries dialog (M3) ============================== */
function WebhookDeliveriesDialog({ webhook, open, onOpenChange }) {
  const { data, isLoading } = useQuery({
    queryKey: ["dev-webhook-deliveries", webhook?.webhook_id],
    queryFn: () =>
      api.get(`/developer/webhooks/${webhook.webhook_id}/deliveries?limit=100`).then((r) => r.data),
    enabled: !!(open && webhook?.webhook_id),
    refetchOnMount: "always",
  });
  const items = data?.items || [];
  const summary = data?.summary || { total: 0, ok: 0, failed: 0 };
  return (
    <VLKAlertDialog
      open={open}
      onOpenChange={onOpenChange}
      title={webhook ? `Deliveries · ${webhook.webhook_id}` : "Deliveries"}
      actions={<VLKButton variant="ghost" onClick={() => onOpenChange(false)}>Close</VLKButton>}
    >
      <div className="space-y-3" data-testid="dev-webhook-deliveries-panel">
        {webhook && (
          <div className="text-[11px] font-mono text-muted-foreground truncate">
            {webhook.url}
          </div>
        )}
        <div className="flex gap-2 text-[11px] font-mono">
          <span className="border border-border rounded-sm px-2 py-0.5">Total: {summary.total}</span>
          <span className="border border-border rounded-sm px-2 py-0.5 text-success">OK: {summary.ok}</span>
          <span className="border border-border rounded-sm px-2 py-0.5 text-destructive">Failed: {summary.failed}</span>
        </div>
        {isLoading ? (
          <VLKSkeleton className="w-full h-40" />
        ) : items.length === 0 ? (
          <VLKEmptyState icon={ListChecks} title="No deliveries recorded"
            description="This webhook has not fired yet. Deliveries appear here once an event is dispatched." />
        ) : (
          <div className="border border-border rounded-sm max-h-96 overflow-y-auto divide-y divide-border">
            {items.map((d) => (
              <div key={d.delivery_id} className="p-2 text-xs flex items-start gap-2"
                   data-testid={`dev-delivery-row-${d.delivery_id}`}>
                <div className="pt-0.5 shrink-0">
                  {d.ok
                    ? <CheckCircle2 className="h-3.5 w-3.5 text-success" />
                    : <XCircle className="h-3.5 w-3.5 text-destructive" />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono font-semibold">{d.event_type || "(unknown event)"}</span>
                    <span className="font-mono text-[10px] text-muted-foreground">
                      HTTP {d.status_code ?? "—"} · attempt {d.attempt} · {d.response_ms ?? 0}ms
                    </span>
                  </div>
                  <div className="font-mono text-[10px] text-muted-foreground truncate">
                    {d.emitted_at} · event {d.event_id}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </VLKAlertDialog>
  );
}

/* ============================== Webhooks Tab ============================== */
function WebhooksTab({ institutions }) {
  const qc = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [reveal, setReveal] = useState(null);
  const [url, setUrl] = useState("");
  const [inst, setInst] = useState("");
  const [events, setEvents] = useState([]);
  const [busy, setBusy] = useState(false);
  const [deliveriesFor, setDeliveriesFor] = useState(null); // webhook doc

  const { data: whData } = useQuery({
    queryKey: ["dev-webhooks"],
    queryFn: () => api.get("/developer/webhooks").then(r => r.data),
  });
  const { data: evData } = useQuery({
    queryKey: ["dev-webhook-events"],
    queryFn: () => api.get("/developer/webhook-events").then(r => r.data),
  });

  const toggleEvent = (e) => setEvents((p) => p.includes(e) ? p.filter(x => x !== e) : [...p, e]);

  const create = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/developer/webhooks", {
        vlk_inst_id: inst, url, events,
      });
      setReveal({ label: `Signing secret · webhook ${data.webhook_id}`, value: data.full_secret_reveal });
      setCreateOpen(false); setUrl(""); setInst(""); setEvents([]);
      toast.success("Webhook created");
      qc.invalidateQueries({ queryKey: ["dev-webhooks"] });
    } catch (e) { toast.error("Failed", { description: apiErrorText(e) }); }
    finally { setBusy(false); }
  };

  const doAction = async (fn, msg) => {
    try { await fn(); toast.success(msg); qc.invalidateQueries({ queryKey: ["dev-webhooks"] }); }
    catch (e) { toast.error("Failed", { description: apiErrorText(e) }); }
  };

  const rotateSecret = (wh) => doAction(async () => {
    const { data } = await api.post(`/developer/webhooks/${wh.webhook_id}/rotate-secret`);
    setReveal({ label: `Rotated secret · ${wh.webhook_id}`, value: data.full_secret_reveal });
  }, "Secret rotated");

  const rows = whData?.items || [];

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="font-display text-lg">Webhooks</div>
          <div className="text-xs text-muted-foreground">HMAC-SHA256 signed. Rotate secrets anytime.</div>
        </div>
        <VLKButton variant="primary" onClick={() => { setCreateOpen(true); setInst(institutions[0]?.vlk_inst_id || ""); setEvents([]); setUrl(""); }} data-testid="dev-webhook-new">
          <Plus className="h-4 w-4" />New webhook
        </VLKButton>
      </div>

      {reveal && <div className="mb-4"><OneTimeReveal label={reveal.label} value={reveal.value} onDismiss={() => setReveal(null)} /></div>}

      {rows.length === 0 ? (
        <VLKEmptyState icon={Webhook} title="No webhooks yet" description="Register a URL to receive events." />
      ) : (
        <div className="space-y-3">
          {rows.map(wh => (
            <VLKCard key={wh.webhook_id} className="p-4">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <VLKStatusBadge status={wh.status === "ACTIVE" ? "active" : "pending"}>{wh.status}</VLKStatusBadge>
                    <code className="font-mono text-xs">{wh.url}</code>
                    <span className="font-mono text-[10px] text-muted-foreground">{wh.webhook_id}</span>
                  </div>
                  <div className="text-xs text-muted-foreground mb-2">{wh.institution_name}</div>
                  <div className="flex flex-wrap gap-1 mb-2">
                    {(wh.events || []).map(e => (
                      <span key={e} className="font-mono text-[10px] uppercase text-muted-foreground border border-border px-1.5 py-0.5 rounded-[2px]">{e}</span>
                    ))}
                  </div>
                  <div className="text-[10px] font-mono text-muted-foreground">
                    Secret: {wh.secret_preview} · Last delivery: {wh.last_delivery_at ? `${new Date(wh.last_delivery_at).toLocaleString()} · HTTP ${wh.last_delivery_status}` : "never"}
                  </div>
                </div>
                <div className="flex gap-1 shrink-0">
                  <VLKButton size="sm" variant="ghost" onClick={() => setDeliveriesFor(wh)}
                             data-testid={`dev-wh-deliveries-${wh.webhook_id}`}>
                    <ListChecks className="h-3.5 w-3.5" />Deliveries
                  </VLKButton>
                  <VLKButton size="sm" variant="ghost" onClick={() => rotateSecret(wh)} data-testid={`dev-wh-rotate-${wh.webhook_id}`}>
                    <RotateCw className="h-3.5 w-3.5" />Rotate secret
                  </VLKButton>
                  {wh.status === "ACTIVE" ? (
                    <VLKButton size="sm" variant="ghost" onClick={() => doAction(() => api.post(`/developer/webhooks/${wh.webhook_id}/pause`), "Paused")}>
                      <PauseCircle className="h-3.5 w-3.5" />Pause
                    </VLKButton>
                  ) : (
                    <VLKButton size="sm" variant="ghost" onClick={() => doAction(() => api.post(`/developer/webhooks/${wh.webhook_id}/resume`), "Resumed")}>
                      <PlayCircle className="h-3.5 w-3.5" />Resume
                    </VLKButton>
                  )}
                  <VLKButton size="sm" variant="ghost" onClick={() => doAction(() => api.delete(`/developer/webhooks/${wh.webhook_id}`), "Deleted")} data-testid={`dev-wh-delete-${wh.webhook_id}`}>
                    <Trash2 className="h-3.5 w-3.5" />Delete
                  </VLKButton>
                </div>
              </div>
            </VLKCard>
          ))}
        </div>
      )}

      <VLKAlertDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        title="Register webhook"
        actions={
          <>
            <VLKButton variant="ghost" onClick={() => setCreateOpen(false)}>Cancel</VLKButton>
            <VLKButton variant="primary" onClick={create} loading={busy} disabled={!url || events.length === 0 || !inst} data-testid="dev-webhook-submit">Create</VLKButton>
          </>
        }
      >
        <div className="space-y-3">
          <div className="space-y-1.5"><VLKLabel>Institution</VLKLabel>
            <VLKSelect value={inst} onChange={(e) => setInst(e.target.value)}>
              {institutions.map(i => <option key={i.vlk_inst_id} value={i.vlk_inst_id}>{i.name}</option>)}
            </VLKSelect>
          </div>
          <div className="space-y-1.5"><VLKLabel>Delivery URL</VLKLabel>
            <VLKInput value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://your-service.example.com/hooks/veralok" className="font-mono" data-testid="dev-webhook-url" />
          </div>
          <div className="space-y-1.5"><VLKLabel>Events ({events.length} selected)</VLKLabel>
            <div className="border border-border rounded-sm p-3 space-y-1">
              {(evData?.events || []).map(e => (
                <label key={e} className="flex items-center gap-2 cursor-pointer hover:bg-secondary/30 rounded-sm p-1">
                  <input type="checkbox" checked={events.includes(e)} onChange={() => toggleEvent(e)} className="accent-foreground" />
                  <span className="font-mono text-xs">{e}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
      </VLKAlertDialog>

      <WebhookDeliveriesDialog
        webhook={deliveriesFor}
        open={!!deliveriesFor}
        onOpenChange={(v) => { if (!v) setDeliveriesFor(null); }}
      />
    </div>
  );
}

/* ============================== Usage Tab ============================== */
function UsageTab() {
  const { data } = useQuery({
    queryKey: ["dev-usage"],
    queryFn: () => api.get("/developer/usage?hours=24").then(r => r.data),
  });
  const buckets = data?.buckets || [];
  const summary = data?.summary || {};
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <VLKKPICard label="Calls · 24h" value={summary.total_calls?.toLocaleString() || "—"} icon={Activity} />
        <VLKKPICard label="Errors · 24h" value={summary.total_errors ?? "—"} icon={Activity} tone={summary.total_errors > 100 ? "danger" : "default"} />
        <VLKKPICard label="Error rate" value={summary.error_rate != null ? `${(summary.error_rate * 100).toFixed(2)}%` : "—"} tone={summary.error_rate > 0.02 ? "warning" : "success"} icon={Gauge} />
        <VLKKPICard label="Avg latency" value={summary.avg_latency_ms ? `${summary.avg_latency_ms} ms` : "—"} icon={Activity} tone={summary.avg_latency_ms > 200 ? "warning" : "info"} />
      </div>
      <VLKCard>
        <VLKCardHeader>
          <VLKCardTitle>Calls per hour · last 24h</VLKCardTitle>
          <VLKCardDescription>Aggregated across all institutions and environments.</VLKCardDescription>
        </VLKCardHeader>
        <VLKCardContent>
          <div className="h-72" style={{ minWidth: 300, minHeight: 220 }}>
            {buckets.length === 0 ? <VLKSkeleton className="w-full h-full" /> : (
              <ResponsiveContainer width="100%" height="100%" minWidth={300} minHeight={220}>
                <BarChart data={buckets}>
                  <XAxis dataKey="hour" tick={{ fontSize: 10, fontFamily: "IBM Plex Mono" }} stroke="hsl(var(--muted-foreground))" />
                  <YAxis tick={{ fontSize: 10, fontFamily: "IBM Plex Mono" }} stroke="hsl(var(--muted-foreground))" />
                  <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 2, fontSize: 12 }} />
                  <Bar dataKey="calls" fill="hsl(var(--chart-2))" radius={0} />
                  <Bar dataKey="errors" fill="hsl(var(--destructive))" radius={0} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </VLKCardContent>
      </VLKCard>
      <VLKCard>
        <VLKCardHeader><VLKCardTitle>Latency</VLKCardTitle></VLKCardHeader>
        <VLKCardContent>
          <div className="h-64" style={{ minWidth: 300, minHeight: 200 }}>
            {buckets.length === 0 ? <VLKSkeleton className="w-full h-full" /> : (
              <ResponsiveContainer width="100%" height="100%" minWidth={300} minHeight={200}>
                <LineChart data={buckets}>
                  <XAxis dataKey="hour" tick={{ fontSize: 10, fontFamily: "IBM Plex Mono" }} stroke="hsl(var(--muted-foreground))" />
                  <YAxis unit=" ms" tick={{ fontSize: 10, fontFamily: "IBM Plex Mono" }} stroke="hsl(var(--muted-foreground))" />
                  <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 2, fontSize: 12 }} />
                  <Line type="monotone" dataKey="avg_ms" stroke="hsl(var(--foreground))" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
        </VLKCardContent>
      </VLKCard>
    </div>
  );
}

/* ============================== Rate Limits / Docs ============================== */
function RateLimitsTab() {
  const { data } = useQuery({
    queryKey: ["dev-rate-limits"],
    queryFn: () => api.get("/developer/rate-limits").then(r => r.data),
  });
  const rows = Object.entries(data?.limits || {}).map(([scope, l]) => ({ scope, ...l }));
  return (
    <VLKCard>
      <VLKCardHeader>
        <VLKCardTitle>Rate limits by scope</VLKCardTitle>
        <VLKCardDescription>Global default limits. Per-institution overrides available on request.</VLKCardDescription>
      </VLKCardHeader>
      <VLKCardContent className="p-0">
        <VLKDataTable
          columns={[
            { key: "scope", header: "Scope", mono: true, render: (r) => <span className="font-mono text-xs">{r.scope}</span> },
            { key: "per_minute", header: "Per minute", align: "right", mono: true, render: (r) => <span className="tabular">{r.per_minute?.toLocaleString?.() ?? r.per_minute}</span> },
            { key: "per_day", header: "Per day", align: "right", mono: true, render: (r) => <span className="tabular">{r.per_day?.toLocaleString?.() ?? r.per_day}</span> },
          ]}
          rows={rows}
          rowKey={(r) => r.scope}
        />
      </VLKCardContent>
    </VLKCard>
  );
}

function DocsTab() {
  const guides = [
    { title: "Quickstart · Verify an identity", body:
`# Prerequisites
- Issue a Sandbox credential from the Credentials tab.
- Note your Institution ID (VLK_INST_XXXXXXX).

# Verify
POST $VERALOK_URL/api/qr/verify
Authorization: Bearer <full_api_key>
Content-Type: application/json

{
  "payload": "qr_XXXX|identity_verification|2026-...",
  "signature": "hex..."
}
` },
    { title: "Signing webhook payloads", body:
`Each event body is signed with your webhook secret using:

    HMAC_SHA256( body_bytes, whsec )  → hex

Compare the value in the X-VeraLok-Signature header. Reject on mismatch.
` },
    { title: "Rotating a credential without downtime", body:
`1. Call POST /api/developer/credentials/{id}/rotate to get the new key.
2. Deploy the new key to your integration (old key still ACTIVE briefly).
3. When traffic on the old key is 0, mark the OLD credential REVOKED.

Server marks the old row as ROTATED — it stops being valid immediately at the
current release; production ADR-0021 introduces a 24h grace window.
` },
  ];
  return (
    <div className="space-y-4">
      <VLKCard>
        <VLKCardHeader>
          <VLKCardTitle>OpenAPI reference</VLKCardTitle>
          <VLKCardDescription>Machine-readable contract for all VeraLok endpoints.</VLKCardDescription>
        </VLKCardHeader>
        <VLKCardContent className="flex gap-3 flex-wrap">
          <a href={`${process.env.REACT_APP_BACKEND_URL}/docs`} target="_blank" rel="noreferrer">
            <VLKButton variant="outline"><BookOpen className="h-4 w-4" />Swagger UI</VLKButton>
          </a>
          <a href={`${process.env.REACT_APP_BACKEND_URL}/openapi.json`} target="_blank" rel="noreferrer">
            <VLKButton variant="outline"><ExternalLink className="h-4 w-4" />openapi.json</VLKButton>
          </a>
        </VLKCardContent>
      </VLKCard>
      <VLKCard>
        <VLKCardHeader>
          <VLKCardTitle>SDK downloads</VLKCardTitle>
          <VLKCardDescription>
            Official SDKs are not published for v1.0. Integrate directly against the OpenAPI contract above using your language's standard HTTP client.
          </VLKCardDescription>
        </VLKCardHeader>
        <VLKCardContent className="flex gap-3 flex-wrap">
          <VLKButton variant="outline" disabled data-testid="dev-sdk-ts">
            <Download className="h-4 w-4" />TypeScript SDK · not available
          </VLKButton>
          <VLKButton variant="outline" disabled data-testid="dev-sdk-py">
            <Download className="h-4 w-4" />Python SDK · not available
          </VLKButton>
          <VLKButton variant="outline" disabled data-testid="dev-sdk-go">
            <Download className="h-4 w-4" />Go SDK · not available
          </VLKButton>
        </VLKCardContent>
      </VLKCard>
      {guides.map(g => (
        <VLKCard key={g.title}>
          <VLKCardHeader><VLKCardTitle>{g.title}</VLKCardTitle></VLKCardHeader>
          <VLKCardContent>
            <pre className="bg-background border border-border rounded-sm p-3 overflow-x-auto text-xs font-mono whitespace-pre-wrap">{g.body}</pre>
          </VLKCardContent>
        </VLKCard>
      ))}
    </div>
  );
}

/* ============================== Main page ============================== */
export default function Developer() {
  const { user: me } = useAuth();
  // Read shell-supplied initial tab hint (set by DeveloperShell before route render).
  // Falls back to "credentials". Cleared after read so subsequent renders don't
  // clobber user-driven tab changes.
  const initialTab = React.useMemo(() => {
    if (typeof window !== "undefined" && window.__vlk_dev_initial_tab) {
      const t = window.__vlk_dev_initial_tab;
      try { delete window.__vlk_dev_initial_tab; } catch (_) { window.__vlk_dev_initial_tab = null; }
      return t;
    }
    return "credentials";
  }, []);
  const [tab, setTab] = useState(initialTab);

  const { data: instData } = useQuery({
    queryKey: ["developer-institutions"],
    queryFn: () => api.get("/institutions").then(r => r.data),
  });
  const institutions = instData?.items || [];

  const { data: credsData } = useQuery({
    queryKey: ["dev-credentials-kpi"],
    queryFn: () => api.get("/developer/credentials").then(r => r.data),
  });
  const kpiActive = (credsData?.items || []).filter(c => c.status === "ACTIVE").length;
  const kpiLive = (credsData?.items || []).filter(c => c.env === "LIVE" && c.status === "ACTIVE").length;
  const kpiRevoked = (credsData?.items || []).filter(c => c.status === "REVOKED").length;

  const { data: whData } = useQuery({
    queryKey: ["dev-webhooks-kpi"],
    queryFn: () => api.get("/developer/webhooks").then(r => r.data),
  });
  const kpiWebhooks = (whData?.items || []).length;

  return (
    <div>
      <VLKPageHeader
        title="Developer portal"
        description="Self-service integration platform · credentials · webhooks · analytics"
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <VLKKPICard label="Active credentials" value={kpiActive} icon={KeyRound} tone="info" testId="dev-kpi-active" />
        <VLKKPICard label="Production keys" value={kpiLive} icon={Shield} tone="warning" />
        <VLKKPICard label="Revoked" value={kpiRevoked} icon={Ban} tone="danger" />
        <VLKKPICard label="Webhooks" value={kpiWebhooks} icon={Webhook} />
      </div>

      <VLKTabs
        value={tab}
        onChange={setTab}
        items={[
          { value: "credentials", label: "Credentials" },
          { value: "webhooks",    label: "Webhooks" },
          { value: "usage",       label: "Usage" },
          { value: "rate-limits", label: "Rate limits" },
          { value: "docs",        label: "Docs & SDKs" },
        ]}
      />

      <div className="mt-6" data-testid={`dev-tab-panel-${tab}`}>
        {tab === "credentials" && <CredentialsTab institutions={institutions} />}
        {tab === "webhooks"    && <WebhooksTab institutions={institutions} />}
        {tab === "usage"       && <UsageTab />}
        {tab === "rate-limits" && <RateLimitsTab />}
        {tab === "docs"        && <DocsTab />}
      </div>
    </div>
  );
}
